import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { PracticeQueue, QueueItem } from '@/lib/types';

type CreatePracticeSessionRequest = {
  instrumentId: string;
  practicedAt: string;
};

export type PracticeSessionQueueItem = {
  practiceSessionQueueItemId: string;
  arrangementId: string;
  userArrangementId: string | null;
  queuePosition: number;
  arrangementName: string;
  pieceName: string;
  keyName: string | null;
  formName: string | null;
  masteryLevelName: string | null;
  masteryLevelSortOrder: number;
  composerName: string | null;
  notes: string | null;
  plannedMinutes: number | null;
  partsCount: number | null;
  structurePattern: string | null;
  practiceTempoBeats: number | null;
};

export type Mood = 'great' | 'good' | 'okay' | 'rough';

export type PracticeSessionResponse = {
  practiceSessionId: string;
  instrumentId: string;
  instrumentName?: string;
  practicedAt: string;
  // Always serialized by the server: null while a session is in progress, set
  // (in minutes) once it has ended. A non-null value means the session is over.
  durationMinutes: number | null;
  notes?: string | null;
  mood?: Mood | null;
  // The create endpoint returns the queue; GET /practice-sessions/{id} returns
  // null here (the queue is fetched separately via usePracticeSessionQueue).
  queue: PracticeSessionQueueItem[] | null;
};

type AddArrangementToSessionRequest = {
  sessionId: string;
  arrangementId: string;
  assessmentLevelId?: string;
  durationMinutes?: number;
  tempoBpm?: number;
  notes?: string;
};

type SessionArrangementResponse = {
  practiceSessionId: string;
  arrangementId: string;
  assessmentLevelId: string | null;
};

export function usePracticeQueue(): ReturnType<typeof useQuery<PracticeQueue>> {
  return useQuery({
    queryKey: ['practice-queue'],
    queryFn: () => api.fetch<PracticeQueue>('/practice-queue'),
  });
}

export function usePracticeSessionQueue(
  sessionId: string | null,
): ReturnType<typeof useQuery<PracticeSessionQueueItem[]>> {
  return useQuery({
    queryKey: ['practice-session-queue', sessionId],
    queryFn: () =>
      api.fetch<PracticeSessionQueueItem[]>(`/practice-sessions/${sessionId}/queue`),
    enabled: sessionId !== null,
  });
}

export function useGetPracticeSessionById(
  sessionId: string | null,
): ReturnType<typeof useQuery<PracticeSessionResponse>> {
  return useQuery({
    queryKey: ['practice-session', sessionId],
    queryFn: () => api.fetch<PracticeSessionResponse>(`/practice-sessions/${sessionId}`),
    enabled: sessionId !== null,
  });
}

export function useCreatePracticeSession(): ReturnType<
  typeof useMutation<PracticeSessionResponse, Error, CreatePracticeSessionRequest>
> {
  return useMutation({
    mutationFn: (data: CreatePracticeSessionRequest) =>
      api.fetch<PracticeSessionResponse>('/practice-sessions', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  });
}

export function useAddArrangementToSession(): ReturnType<
  typeof useMutation<SessionArrangementResponse, Error, AddArrangementToSessionRequest>
> {
  return useMutation({
    mutationFn: ({ sessionId, ...data }: AddArrangementToSessionRequest) =>
      api.fetch<SessionArrangementResponse>(`/practice-sessions/${sessionId}/arrangements`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    // No invalidation here — the practice-queue should not churn during a session.
    // The active phase reads from the session snapshot, not /practice-queue.
    // /practice-queue is invalidated when the session completes (see practice.tsx).
  });
}

export function snapshotItemToQueueItem(item: PracticeSessionQueueItem): QueueItem {
  return {
    userArrangementId: item.userArrangementId ?? item.arrangementId,
    arrangementId: item.arrangementId,
    arrangementName: item.arrangementName,
    pieceName: item.pieceName,
    composerName: item.composerName,
    keyName: item.keyName,
    formName: item.formName,
    masteryLevelName: item.masteryLevelName,
    masteryLevelSortOrder: item.masteryLevelSortOrder,
    queueStatusName: '',
    pinned: false,
    dueNow: false,
    lastPracticedAt: null,
    daysSinceLastPracticed: 0,
    lastAssessmentName: null,
    notes: item.notes,
    plannedMinutes: item.plannedMinutes,
    partsCount: item.partsCount,
    structurePattern: item.structurePattern,
    practiceTempoBeats: item.practiceTempoBeats,
  };
}
